import * as t from 'io-ts';
import { NonEmptyString } from 'io-ts-types';
import { fromEnum } from '../../responseUtil';
import { VehicleCodec } from './vehicleCompound.types';
import { ApiVehicleType } from '../sharedVehicleApi.types';
import { AddressCodec } from '../address.types';
import { MeansOfTransportTypeCodec } from '../meansOfTransportType.types';
import { LocationCodec } from './location.types';

const TransportInformationCodec = t.intersection([
    t.type({ means_of_transport_type: MeansOfTransportTypeCodec }),
    t.partial({
        means_of_transport_id: t.string,
        means_of_transport_name: t.string,
        trailer_id: t.string,
        journey_id: t.string,
        load_id: t.string,
    }),
]);

const CompoundLocationCodec = t.type({ identifier: NonEmptyString, address: AddressCodec, time_zone: NonEmptyString });

const FreightForwarderCodec = t.intersection([
    t.type({ name: NonEmptyString }),
    t.partial({ additional_name: NonEmptyString }),
]);

const ExpectedArrivalCodec = t.intersection([
    t.type({
        expected_arrival_at: NonEmptyString,
        transport_information: TransportInformationCodec,
        is_cancelled: t.boolean,
    }),
    t.partial({
        freight_forwarder: FreightForwarderCodec,
        arrival_from: LocationCodec,
    }),
]);

const ArrivalCodec = t.intersection([
    t.type({
        arrival_completed_at: NonEmptyString,
        transport_information: TransportInformationCodec,
        is_cancelled: t.boolean,
    }),
    t.partial({
        freight_forwarder: FreightForwarderCodec,
        arrival_from: LocationCodec,
    }),
]);

const ExpectedDepartureCodec = t.intersection([
    t.type({
        expected_departure_at: NonEmptyString,
        transport_information: TransportInformationCodec,
        is_cancelled: t.boolean,
    }),
    t.partial({
        freight_forwarder: FreightForwarderCodec,
        departure_to: LocationCodec,
    }),
]);

const DepartureCodec = t.intersection([
    t.type({
        departure_completed_at: NonEmptyString,
        transport_information: TransportInformationCodec,
        is_cancelled: t.boolean,
    }),
    t.partial({
        freight_forwarder: FreightForwarderCodec,
        departure_to: LocationCodec,
    }),
]);

export enum ApiAllowedAction {
    REPORT_ARRIVAL = 'report-arrival',
    REPORT_ANNOUNCED_DEPARTURE = 'report-announced-departure',
    REPORT_UNANNOUNCED_DEPARTURE = 'report-unannounced-departure',
}

const CompoundVehicleCodec = t.type({
    id: NonEmptyString,
    compound_location: CompoundLocationCodec,
    vehicle: VehicleCodec,
    allowed_actions: t.array(fromEnum<ApiAllowedAction>('ApiAllowedAction', ApiAllowedAction)),
});

const IncomingVehicleCodec = t.intersection([CompoundVehicleCodec, t.type({ arrival: ExpectedArrivalCodec })]);

const InventoryVehicleCodec = t.intersection([CompoundVehicleCodec, t.type({ arrival: ArrivalCodec })]);

const OutgoingVehicleCodec = t.intersection([CompoundVehicleCodec, t.type({ departure: ExpectedDepartureCodec })]);

const DepartedVehicleCodec = t.intersection([
    CompoundVehicleCodec,
    t.type({ departure: DepartureCodec }),
    t.partial({ arrival: ArrivalCodec }),
]);

export const IncomingVehiclesPageCodec = t.type({
    items: t.array(IncomingVehicleCodec),
    has_more: t.boolean,
});

export const InventoryVehiclesPageCodec = t.type({
    items: t.array(InventoryVehicleCodec),
    has_more: t.boolean,
});

export const OutgoingVehiclesPageCodec = t.type({
    items: t.array(OutgoingVehicleCodec),
    has_more: t.boolean,
});

export const DepartedVehiclesPageCodec = t.type({
    items: t.array(DepartedVehicleCodec),
    has_more: t.boolean,
});

export const VehicleBulkSelectionCodec = t.type({
    compound_vehicle_ids: t.array(NonEmptyString),
});

export const LoadValidationRequestCodec = t.type({
    compound_vehicle_ids: t.array(NonEmptyString),
    additional_vins: t.array(NonEmptyString),
});

export enum ApiLoadPreviewErrorCode {
    UNKNOWN_VIN = 'unknown-vin',
    INCORRECT_STATE = 'incorrect-state',
    OTHER_SUPPLIER = 'other-supplier',
    CANCELLED = 'cancelled',
    HAS_OUTGOING = 'has-outgoing',
    DIFFERENT_MEANS_TYPE = 'different-means-type',
    VEHICLE_IN_INCOMING = 'vehicle-in-incoming',
    INVALID_LOCATION = 'invalid-location',
    MISSING_PERMISSION = 'missing-permission',
    DUPLICATE_VIN = 'duplicate-vin',
}

const LoadPreviewErrorCodec = t.type({
    error_code: fromEnum<ApiLoadPreviewErrorCode>('ApiLoadPreviewErrorCodeErrorCode', ApiLoadPreviewErrorCode),
    vehicles: t.array(t.string),
});

const VehicleInUnannouncedLoadPreviewCodec = t.intersection([
    t.type({
        id: NonEmptyString,
        vin: NonEmptyString,
        model_name: NonEmptyString,
    }),
    t.partial({
        vehicle_type: fromEnum<ApiVehicleType>('ApiVehicleType', ApiVehicleType),
        final_destination: LocationCodec,
        model_version: NonEmptyString,
        manufacturer: NonEmptyString,
    }),
]);

const VehicleInLoadPreviewCodec = t.intersection([
    t.type({
        id: NonEmptyString,
        vin: NonEmptyString,
        model_name: NonEmptyString,
        means_of_transport_type: MeansOfTransportTypeCodec,
    }),
    t.partial({
        vehicle_type: fromEnum<ApiVehicleType>('ApiVehicleType', ApiVehicleType),
        final_destination: LocationCodec,
        manufacturer: NonEmptyString,
        model_version: NonEmptyString,
        means_of_transport_id: t.string,
        trailer_id: t.string,
    }),
]);

export const UnannouncedLoadPreviewDeparturesCodec = t.type({
    vehicles: t.array(VehicleInUnannouncedLoadPreviewCodec),
    invalid_vehicles: t.array(LoadPreviewErrorCodec),
    compound_time_zone: t.string,
});

export const LoadPreviewCodec = t.type({
    vehicles: t.array(VehicleInLoadPreviewCodec),
    invalid_vehicles: t.array(LoadPreviewErrorCodec),
    compound_time_zone: t.string,
});

export type ApiTransportInformation = t.TypeOf<typeof TransportInformationCodec>;
export type ApiCompoundLocation = t.TypeOf<typeof CompoundLocationCodec>;
export type ApiExpectedArrival = t.TypeOf<typeof ExpectedArrivalCodec>;
export type ApiArrival = t.TypeOf<typeof ArrivalCodec>;
export type ApiExpectedDeparture = t.TypeOf<typeof ExpectedDepartureCodec>;
export type ApiDeparture = t.TypeOf<typeof DepartureCodec>;
export type ApiFreightForwarder = t.TypeOf<typeof FreightForwarderCodec>;
export type ApiIncomingVehicle = t.TypeOf<typeof IncomingVehicleCodec>;
export type ApiInventoryVehicle = t.TypeOf<typeof InventoryVehicleCodec>;
export type ApiOutgoingVehicle = t.TypeOf<typeof OutgoingVehicleCodec>;
export type ApiDepartedVehicle = t.TypeOf<typeof DepartedVehicleCodec>;

export type ApiIncomingVehiclesPage = t.TypeOf<typeof IncomingVehiclesPageCodec>;
export type ApiInventoryVehiclesPage = t.TypeOf<typeof InventoryVehiclesPageCodec>;
export type ApiOutgoingVehiclesPage = t.TypeOf<typeof OutgoingVehiclesPageCodec>;
export type ApiDepartedVehiclesPage = t.TypeOf<typeof DepartedVehiclesPageCodec>;
export type ApiVehicleBulkSelection = t.TypeOf<typeof VehicleBulkSelectionCodec>;
export type ApiLoadValidationRequest = t.TypeOf<typeof LoadValidationRequestCodec>;

// only used as request body, never validated, thus not necessary as a codec
export type ApiCompletionStatusChange = {
    status_at: string;
    compound_vehicle_ids: string[];
    transport_information: ApiTransportInformation;
};

export type ApiUnannouncedLoadPreviewDepartures = t.TypeOf<typeof UnannouncedLoadPreviewDeparturesCodec>;
export type ApiVehicleInUnannouncedLoadPreview = t.TypeOf<typeof VehicleInUnannouncedLoadPreviewCodec>;
export type ApiLoadPreview = t.TypeOf<typeof LoadPreviewCodec>;
export type ApiVehicleInLoadPreview = t.TypeOf<typeof VehicleInLoadPreviewCodec>;
export type ApiLoadPreviewError = t.TypeOf<typeof LoadPreviewErrorCodec>;
